import { inject, onUnmounted, provide, ref, unref, watch } from 'vue'

// Breadcrumb provide/inject for <AdminAppLayout>.
//
// The layout OWNS and renders the entire breadcrumb (Context › Section › Page is
// derived from `nav` + `activePage`). Descendant page components publish extra
// trailing crumbs via `useBreadcrumb(source)`; they never render their own.
//
// A crumb is a string or `{ label, options?, onClose? }` (see PageHeader). The
// `source` may be a ref, a getter, or a static array so labels can update
// reactively (e.g. after a record loads). Registration clears on unmount.

const BREADCRUMB_KEY = Symbol('adminAppBreadcrumb')

// Called once by AdminAppLayout. Returns the reactive `subCrumbs` it renders.
export function provideBreadcrumb() {
  const subCrumbs = ref(null)
  provide(BREADCRUMB_KEY, (labels) => {
    subCrumbs.value = labels ?? null
  })
  return { subCrumbs }
}

// Called by page components to publish their trailing crumbs.
export function useBreadcrumb(source) {
  const register = inject(BREADCRUMB_KEY, null)
  if (!register) return // used outside the layout — no-op

  watch(
    () => (typeof source === 'function' ? source() : unref(source)),
    (labels) => register(labels ?? null),
    { immediate: true, deep: true },
  )
  onUnmounted(() => register(null))
}
